import { typographyCss } from '@creditornot/cb-ingredients';
import { ModalContent, ModalHeader } from '@creditornot/cb-modal';
import { Tag } from '@creditornot/cb-tag';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import React, { ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { CreditAcquisition } from 'apis/restaurant-api/merchant-admin/credits/types';
import { Purchase, ReviewAttribution } from 'apis/restaurant-api/merchant-admin/purchases/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { useLocaleUtils, useRetailContext } from 'common/hooks';
import SentOfferDetails from 'modules/insights-v2/components/ratings/CompensationOffers/SentOfferDetails';

import { DELIVERY_PROVIDERS, DELIVERY_TYPES } from './constants';
import OldPurchaseBreakdown from './purchase-breakdown/OldPurchaseBreakdown';
import PurchaseBreakdown from './purchase-breakdown/PurchaseBreakdown';

type Props = {
  menuItemCurrency: Currency;
  onClose: () => void;
  purchase: Purchase;
  timezone: string;
  showDeliveryFees: boolean;
  deliveryFeesIncludeServiceFees: boolean;
  showTaxID: boolean;
  orderComment?: string;
  compensationOffer?: CreditAcquisition;
  compensationOfferBanner?: ReactNode;
};

const Root = styled.div`
  color: ${({ theme }) => theme.cbSemanticColors.textPrimary};
  /* NOTE: Extra padding to fit the Intercom chat bubble */
  padding-block-end: 70px;
`;

const Section = styled.div`
  margin-block-end: 16px;
`;

const FieldLabel = styled.div`
  ${typographyCss.Small}
  color: ${({ theme }) => theme.cbSemanticColors.textSecondary};
  margin-block-end: 4px;
`;

const FieldValue = styled.div`
  ${typographyCss.Body3}
`;

const MultiplicationSign = styled.div`
  ::before {
    content: '×';
    margin: 0 4px;
  }
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemInfo = styled.div`
  border-block-end: 1px solid ${({ theme }) => theme.cbSemanticColors.borderPrimary};
  padding: 12px 0;
`;

const ItemInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ItemCount = styled.div.attrs({ fontWeight: 'bold' })`
  ${typographyCss.Body3}
  display: flex;
`;

const ItemName = styled.div`
  ${typographyCss.Body3}
  flex: 1;
  ${({ theme }) =>
    theme.cbDirection === 'ltr'
      ? css`
          padding-inline-end: 16px;
        `
      : css`
          padding-inline-start: 16px;
        `}
`;

const ItemPrice = styled.div`
  ${typographyCss.Body3}
`;

const Attributions = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-block-start: 4px;
  margin-block-end: -8px;
`;

const Attribution = styled(Tag).attrs({ variant: 'blue' })`
  margin: 0 8px 8px 0;
`;

const OfferFieldValue = styled(FieldValue)`
  padding-block-start: 4px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
`;

const TotalLabel = styled.div.attrs({ fontWeight: 'bold' })`
  ${typographyCss.Body3}
`;

const TotalAmount = styled.div.attrs({ fontWeight: 'bold' })`
  ${typographyCss.Body3}
`;

const Title = styled.div`
  ${typographyCss.Title1}
  margin-block-end: 12px;
  margin-block-start: 32px;
`;

const OrderInformationTitle = styled.div`
  ${typographyCss.Title1}
  margin-block-end: 12px;
  margin-block-start: 16px;
`;

const ModalHeading = styled.div`
  ${typographyCss.Heading6}
`;

const PurchaseDetails = ({
  menuItemCurrency,
  purchase,
  timezone,
  showDeliveryFees,
  deliveryFeesIncludeServiceFees,
  showTaxID,
  orderComment,
  onClose,
  compensationOffer,
  compensationOfferBanner,
}: Props) => {
  const { formatWeightQuantity } = useLocaleUtils();
  const isRetailContext = useRetailContext();
  const deliveryFee = purchase.merchant_pricing?.fees?.total_fees.full_amount
    ? purchase.merchant_pricing.fees.total_fees.full_amount
    : purchase.merchant_pricing?.total_fees?.full_amount ?? purchase.delivery.fee;
  const deliveryFeeAmount = new Decimal(deliveryFee?.amount ?? '0');
  const deprecatedItemTotalPrice = new Decimal(purchase.price.amount);
  const totalPriceWithoutFee =
    purchase?.merchant_pricing?.basket?.total_price?.full_amount &&
    purchase?.merchant_pricing?.fees?.total_fees.full_amount.amount
      ? new Decimal(purchase.merchant_pricing.basket.total_price.full_amount.amount)
      : deprecatedItemTotalPrice;
  const totalPrice = showDeliveryFees ? totalPriceWithoutFee.add(deliveryFeeAmount) : totalPriceWithoutFee;
  const finalPrice =
    purchase?.merchant_pricing?.basket?.final_price?.full_amount &&
    purchase?.merchant_pricing?.fees?.final_fees.full_amount.amount
      ? new Decimal(purchase.merchant_pricing.basket.final_price.full_amount.amount).add(
          new Decimal(purchase.merchant_pricing.fees.final_fees.full_amount.amount),
        )
      : null;

  const deliveryDiscountAmount = new Decimal(purchase.merchant_pricing?.fees?.discount.full_amount.amount ?? '0');
  const basketDiscountAmount = new Decimal(purchase.merchant_pricing?.basket?.discount.full_amount.amount ?? '0');
  const totalDiscountAmount = new Decimal(
    purchase.merchant_pricing?.total_discounts?.full_amount.amount ?? deliveryDiscountAmount.add(basketDiscountAmount),
  );

  const reviewAttributionTranslations = useMemo<{ [key in ReviewAttribution]: string }>(
    () => ({
      appearance: 'purchases.attributions.appearance',
      atmosphere: 'purchases.attributions.atmosphere',
      'cold food': 'purchases.attributions.coldFood',
      comment: 'purchases.attributions.comment',
      courier: 'purchases.attributions.courier',
      'delivery instructions': 'purchases.attributions.deliveryInstructions',
      'delivery time': 'purchases.attributions.deliveryTime',
      estimate: 'purchases.attributions.estimate',
      experience: 'purchases.attributions.experience',
      flavour: 'purchases.attributions.flavour',
      food: 'purchases.attributions.food',
      'had to queue': 'purchases.attributions.hadToQueue',
      'missing item': 'purchases.attributions.missingItem',
      packaging: 'purchases.attributions.packaging',
      'portion size': 'purchases.attributions.portionSize',
      'product damaged': 'purchases.attributions.productDamaged',
      quality: 'purchases.attributions.quality',
      selection: 'purchases.attributions.selection',
      staff: 'purchases.attributions.staff',
      texture: 'purchases.attributions.texture',
      'value for money': 'purchases.attributions.valueForMoney',
      venue: isRetailContext ? 'purchases.attributions.venue-retail' : 'purchases.attributions.venue',
    }),
    [isRetailContext],
  );

  return (
    <Root>
      <ModalHeader onCloseClick={onClose}>
        <ModalHeading>
          <FormattedMessage id="purchases.details-heading" />
        </ModalHeading>
      </ModalHeader>

      <ModalContent>
        <OrderInformationTitle>
          <FormattedMessage id="purchases.order-information" />
        </OrderInformationTitle>

        {showTaxID && (
          <Section>
            <FieldLabel>TaxID (NIP)</FieldLabel>
            <FieldValue>{purchase.customer_tax_id ? purchase.customer_tax_id : '-'}</FieldValue>
          </Section>
        )}

        <Section>
          <FieldLabel>
            <FormattedMessage id="purchases.headings.order-number" />
          </FieldLabel>
          <FieldValue>#{purchase.order_number}</FieldValue>
        </Section>

        {purchase.daas_order !== null && (
          <Section>
            <FieldLabel>
              <FormattedMessage id="purchases.tracking-link" />
            </FieldLabel>
            <FieldValue>
              <a href={purchase.daas_order.tracking_details.url}>{purchase.daas_order.tracking_details.id}</a>
            </FieldValue>
          </Section>
        )}

        <Section>
          <FieldLabel>
            <FormattedMessage id="purchases.headings.customer" />
          </FieldLabel>
          <FieldValue>{purchase.consumer_name}</FieldValue>
        </Section>

        <Section>
          <FieldLabel>
            <FormattedMessage id="purchases.delivery-type" />
          </FieldLabel>
          <FieldValue>
            <FormattedMessage
              id={
                purchase.delivery_provider_type
                  ? DELIVERY_PROVIDERS[purchase.delivery_provider_type]
                  : DELIVERY_TYPES[purchase.delivery.type]
              }
            />
          </FieldValue>
        </Section>

        {purchase.preorder !== null && (
          <Section>
            <FieldLabel>
              <FormattedMessage id="purchases.preorder-time" />
            </FieldLabel>
            <FieldValue>
              {DateTime.fromISO(purchase.preorder.time, { zone: timezone }).toFormat('dd.MM.yyyy HH:mm')}
            </FieldValue>
          </Section>
        )}

        <Section>
          <FieldLabel>
            <FormattedMessage id="purchases.order-placed" />
          </FieldLabel>
          <FieldValue>
            {DateTime.fromISO(purchase.created_at, { zone: timezone }).toFormat('dd.MM.yyyy HH:mm')}
          </FieldValue>
        </Section>

        {purchase.consumer_comment.length > 0 && (
          <Section>
            <FieldLabel>
              <FormattedMessage id="purchases.headings.order-comment" />
            </FieldLabel>
            <FieldValue>{purchase.consumer_comment}</FieldValue>
          </Section>
        )}

        {purchase.review !== null && (
          <>
            {purchase.review.score !== null && (
              <Section>
                <FieldLabel>
                  <FormattedMessage id="purchases.review-score" />
                </FieldLabel>
                <FieldValue>{purchase.review.score} / 5</FieldValue>
              </Section>
            )}
            {purchase.review.attributions.length > 0 && (
              <Section>
                <FieldLabel>
                  {typeof purchase.review.score === 'number' ? (
                    purchase.review.score < 5 ? (
                      <FormattedMessage id="purchases.headings.attributions-bad" />
                    ) : (
                      <FormattedMessage id="purchases.headings.attributions-good" />
                    )
                  ) : (
                    <FormattedMessage id="purchases.headings.attributions" />
                  )}
                </FieldLabel>
                <Attributions>
                  {purchase.review.attributions.map(attribution => (
                    <Attribution key={attribution}>
                      <FormattedMessage
                        id={reviewAttributionTranslations[attribution.toLowerCase() as ReviewAttribution]}
                      />
                    </Attribution>
                  ))}
                </Attributions>
              </Section>
            )}
            {purchase.review.comment !== '' && (
              <Section>
                <FieldLabel>
                  <FormattedMessage id="purchases.review-comment" />
                </FieldLabel>
                <FieldValue>{purchase.review.comment}</FieldValue>
              </Section>
            )}
            {compensationOffer ? (
              <Section>
                <FieldLabel>
                  <FormattedMessage id="purchases.headings.review-compensation-offer" />
                </FieldLabel>
                <OfferFieldValue>
                  <SentOfferDetails timezone={timezone} sentOffer={compensationOffer} />
                </OfferFieldValue>
              </Section>
            ) : (
              compensationOfferBanner
            )}
          </>
        )}

        {purchase.items_missing.length > 0 && (
          <>
            <Title>
              <FormattedMessage id="purchases.headings.missing-order-items-details" />{' '}
            </Title>
            <Section>
              {purchase.items_missing.map((missingItem, index) => {
                const isPartiallyMissing = purchase.items.some(
                  item =>
                    item.id === missingItem.id &&
                    (missingItem.weighted_item_info?.count || 0) <
                      (item.weighted_item_info?.originally_purchased_count || 0),
                );

                return (
                  <Item key={`${missingItem.id}-${index}`}>
                    <ItemInfo>
                      <ItemInfoRow>
                        {missingItem.weighted_item_info && (
                          <>
                            {!isPartiallyMissing && (
                              <ItemCount>
                                {missingItem.weighted_item_info.purchased_weight_in_grams &&
                                  formatWeightQuantity(missingItem.weighted_item_info.purchased_weight_in_grams, {
                                    unitDisplay: 'narrow',
                                  })}{' '}
                                <MultiplicationSign />
                              </ItemCount>
                            )}
                            <ItemName>
                              {missingItem.name}
                              {missingItem.weighted_item_info.count &&
                                missingItem.weighted_item_info.weighted_item_input_type === 'number_of_items' && (
                                  <>
                                    {' '}
                                    (
                                    <FormattedMessage
                                      id="purchases.display-unit-pc"
                                      values={{
                                        unit: missingItem.weighted_item_info.count,
                                      }}
                                    />
                                    )
                                  </>
                                )}
                            </ItemName>
                          </>
                        )}

                        {!missingItem.weighted_item_info && (
                          <>
                            <ItemCount>
                              {missingItem.count} <MultiplicationSign />
                            </ItemCount>{' '}
                            <ItemName>{missingItem.name}</ItemName>
                          </>
                        )}
                        <ItemPrice>
                          <MonetaryAmount
                            amount={parseFloat(missingItem.total_price.amount)}
                            currency={missingItem.total_price.currency}
                          />
                        </ItemPrice>
                      </ItemInfoRow>
                    </ItemInfo>
                  </Item>
                );
              })}
            </Section>
          </>
        )}

        <Section>
          <Title>
            <FormattedMessage id="purchases.headings.order-details" />
          </Title>

          <>
            {!purchase.merchant_pricing?.basket && (
              <OldPurchaseBreakdown items={purchase.items} basket={purchase.merchant_pricing?.basket} />
            )}
            {purchase.merchant_pricing?.basket && (
              <PurchaseBreakdown items={purchase.items} basket={purchase.merchant_pricing?.basket} />
            )}
          </>
        </Section>

        <Section>
          {showDeliveryFees &&
            purchase.delivery.type === 'homedelivery' &&
            !deliveryFeeAmount.isZero() &&
            deliveryFee?.currency && (
              <SummaryRow>
                <FieldValue>
                  {deliveryFeesIncludeServiceFees ? (
                    <FormattedMessage id="purchases.delivery-and-service" />
                  ) : (
                    <FormattedMessage id="purchases.delivery-types.homedelivery" />
                  )}
                </FieldValue>
                <FieldValue>
                  <MonetaryAmount amount={deliveryFeeAmount.toNumber()} currency={deliveryFee.currency} />
                </FieldValue>
              </SummaryRow>
            )}
          <SummaryRow>
            <TotalLabel>
              <FormattedMessage id="purchases.labels.order-total" values={{ currency: menuItemCurrency }} />
            </TotalLabel>
            <TotalAmount>
              <MonetaryAmount amount={totalPrice.toNumber()} currency={menuItemCurrency} />
            </TotalAmount>
          </SummaryRow>
        </Section>

        {!totalDiscountAmount.isZero() && (
          <SummaryRow>
            <FieldValue>
              <FormattedMessage id="purchases.discount.sponsored" />
            </FieldValue>
            <FieldValue>
              <MonetaryAmount amount={totalDiscountAmount.toNumber()} currency={menuItemCurrency} />
            </FieldValue>
          </SummaryRow>
        )}

        {!totalDiscountAmount.isZero() && finalPrice && (
          <SummaryRow>
            <TotalLabel>
              <FormattedMessage
                id="purchases.labels.order-total-after-deductions"
                values={{ currency: menuItemCurrency }}
              />
            </TotalLabel>
            <TotalAmount>
              <MonetaryAmount amount={finalPrice.toNumber()} currency={menuItemCurrency} />
            </TotalAmount>
          </SummaryRow>
        )}

        {orderComment && (
          <Section>
            <FieldLabel>
              <FormattedMessage id="purchases.headings.order-comment" />
            </FieldLabel>
            <FieldValue>{orderComment}</FieldValue>
          </Section>
        )}
      </ModalContent>
    </Root>
  );
};

export default PurchaseDetails;
